<script setup lang="ts">
import { computed, ref } from 'vue'
import { Check, Headphones, LoaderCircle, LockKeyhole, Music2, Pause, Play, Trash2, Upload, Volume2, VolumeX, X } from '@lucide/vue'
import { standaloneMode } from '../api'
import { auth, preferences, toggleSound } from '../state'
import {
  closeMusicLibrary,
  music,
  removeMusicTrack,
  selectAndPlayTrack,
  setMusicVolume,
  toggleBackgroundMusic,
  uploadMusic,
} from '../music'

const input = ref<HTMLInputElement | null>(null)
const selectedTrack = computed(() => music.tracks.find((item) => item.id === music.selectedTrackId) || null)

function sizeLabel(bytes: number) {
  return bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.ceil(bytes / 1024)} KB`
}

async function chooseFile(event: Event) {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  if (file) await uploadMusic(file)
  target.value = ''
}
</script>

<template>
  <Teleport to="body">
    <Transition name="music-panel">
      <div v-if="music.panelOpen" class="music-backdrop" @click.self="closeMusicLibrary">
        <section class="music-panel" role="dialog" aria-modal="true" aria-labelledby="music-title">
          <header class="music-panel-head">
            <div class="music-title-mark"><Music2 /></div>
            <div><p>PERSONAL SOUND</p><h2 id="music-title">我的背景音乐</h2></div>
            <button class="music-close" aria-label="关闭音乐窗口" @click="closeMusicLibrary"><X /></button>
          </header>

          <div class="music-private-note"><LockKeyhole /><span>私人设置，仅当前账号可见，不会改变问卷或影响其他填写者。</span></div>

          <template v-if="!auth.user">
            <div class="music-empty"><Headphones /><h3>登录后使用私人音乐库</h3><p>上传的音乐只属于你的账号。</p><RouterLink class="button button-dark" to="/login" @click="closeMusicLibrary">前往登录</RouterLink></div>
          </template>
          <template v-else-if="standaloneMode">
            <div class="music-empty"><Headphones /><h3>请在完整版中上传音乐</h3><p>单文件版没有服务器空间，无法长期保存音乐文件。</p></div>
          </template>
          <template v-else>
            <div class="music-now-playing">
              <button class="music-play" :disabled="music.loading" :aria-label="music.playing ? '暂停背景音乐' : '播放背景音乐'" @click="toggleBackgroundMusic">
                <LoaderCircle v-if="music.loading" class="spin"/><Pause v-else-if="music.playing"/><Play v-else/>
              </button>
              <div><small>{{ music.playing ? '正在循环播放' : '当前选择' }}</small><strong>{{ selectedTrack?.title || '尚未选择音乐' }}</strong></div>
              <span v-if="music.playing" class="music-equalizer" aria-hidden="true"><i/><i/><i/></span>
            </div>

            <label class="music-volume">
              <VolumeX v-if="music.volume === 0"/><Volume2 v-else/>
              <input type="range" min="0" max="100" :value="music.volume" aria-label="背景音乐音量" @input="setMusicVolume(Number(($event.target as HTMLInputElement).value))" />
              <span>{{ music.volume }}%</span>
            </label>

            <div class="music-library-head"><div><strong>私人曲库</strong><small>{{ music.tracks.length }} / {{ music.limits.maxTracks }} 首</small></div><button class="music-upload" :disabled="music.uploading" @click="input?.click()"><LoaderCircle v-if="music.uploading" class="spin"/><Upload v-else/>{{ music.uploading ? '上传中' : '上传音乐' }}</button><input ref="input" hidden type="file" accept=".mp3,.wav,.ogg,.m4a,audio/mpeg,audio/wav,audio/ogg,audio/mp4" @change="chooseFile"/></div>

            <div v-if="music.tracks.length" class="music-track-list">
              <article v-for="track in music.tracks" :key="track.id" :class="{ selected: track.id === music.selectedTrackId }">
                <button class="music-track-main" @click="selectAndPlayTrack(track.id)">
                  <span class="music-track-icon"><Pause v-if="music.playing && track.id === music.selectedTrackId"/><Play v-else/></span>
                  <span><strong>{{ track.title }}</strong><small>{{ sizeLabel(track.sizeBytes) }} · 点击播放</small></span>
                  <Check v-if="track.id === music.selectedTrackId" class="music-track-check"/>
                </button>
                <button class="music-delete" :aria-label="`删除 ${track.title}`" @click="removeMusicTrack(track.id)"><Trash2/></button>
              </article>
            </div>
            <div v-else-if="!music.loading" class="music-empty compact"><Music2/><p>还没有音乐，上传一首你喜欢的曲目。</p></div>

            <label class="interface-sound-row"><span><strong>界面提示音</strong><small>选择答案与进入下一题时的轻提示</small></span><input type="checkbox" :checked="preferences.sound" @change="toggleSound"/><i/></label>

            <p v-if="music.message" class="music-message success">{{ music.message }}</p>
            <p v-if="music.error" class="music-message error">{{ music.error }}</p>
            <p class="music-format-note">支持 MP3、WAV、OGG、M4A，单首不超过 20 MB。</p>
          </template>
        </section>
      </div>
    </Transition>
  </Teleport>
</template>
